import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scrollTo } from 'platform/utilities/scroll';
import {
  setDestructiveActionPending,
  setPendingDestructiveNavigation,
} from '../actions/destructiveModal';
import {
  selectPendingDestructiveNavigation,
  selectPendingDestructiveNavigationTriggerOrigin,
} from '../selectors/destructiveModal';
import { DESTRUCTIVE_NAV_TRIGGER_ORIGINS } from '../constants';

/**
 * @typedef {Object} DestructiveConfirmationControls
 * @property {boolean} modalVisible Whether the destructive confirmation modal is visible.
 * @property {boolean} alertVisible Whether the post-confirmation success alert is visible.
 * @property {boolean} isSidenavTriggered True when the modal was triggered by side nav navigation.
 * @property {() => boolean} triggerIfNeeded Opens the modal when destructive data exists,
 * returns true if modal opens, false when normal progression continues.
 * @property {() => void} confirmAndDelete Confirms the destructive action,
 * clears any pending navigation state, and shows the success alert.
 * @property {() => void} cancelAndRevert Cancels the destructive action,
 * invokes cancel behavior, and hides the modal.
 * @property {() => void} dismissAlert Hides the success alert.
 */

/**
 * Shared hook for pages with destructive confirmation modals.
 *
 * Manages local modal/alert visibility and syncs an `isDestructiveActionPending`
 * flag to Redux so external features (e.g., side nav) can intercept navigation.
 *
 * When an external navigator (side nav) sets `pendingNavigation` in Redux, the
 * hook automatically opens the modal. On confirm the hook calls `onConfirm`,
 * clears `pendingNavigation`, and shows the success alert. Navigation happens
 * only on a subsequent user-initiated nav action.
 *
 * @param {Object} config
 * @param {Function} config.hasDestructiveData - Returns true when the page has
 *   data that would be destroyed by the pending action.
 * @param {Function} config.onConfirm - Called when the user confirms destruction.
 *   Should delete the relevant form data.
 * @param {Function} [config.onCancel] - Called when the user cancels. Use to
 *   revert a selection (e.g., re-check a checkbox). Defaults to no-op.
 * @param {Function} [config.onProceed] - Called when there is no destructive
 *   data and the page should advance normally.
 * @returns {DestructiveConfirmationControls}
 */
const useDestructiveConfirmation = ({
  hasDestructiveData,
  onConfirm,
  onCancel = () => {},
  onProceed,
}) => {
  const dispatch = useDispatch();
  const pendingNavigation = useSelector(selectPendingDestructiveNavigation);
  const pendingNavigationTriggerOrigin = useSelector(
    selectPendingDestructiveNavigationTriggerOrigin,
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [triggerOrigin, setTriggerOrigin] = useState(null);

  // Track whether the modal was opened by an external navigator so we know
  // how to handle the confirm action.
  const externalTriggerRef = useRef(false);

  // Scroll to success alert when it becomes visible.
  useEffect(() => {
    if (alertVisible) {
      scrollTo('success-alert');
    }
  }, [alertVisible]);

  // Sync the Redux flag whenever the destructive-data check result changes.
  // Clean up on unmount so stale coordination state doesn't leak across pages.
  useEffect(
    () => {
      const pending = hasDestructiveData();
      dispatch(setDestructiveActionPending(pending));

      return () => {
        dispatch(setDestructiveActionPending(false));
        dispatch(setPendingDestructiveNavigation({}));
      };
    },
    // The caller should memoize or rely on the hook's dependency array to
    // control re-evaluation. We intentionally call on every render so the
    // flag stays fresh as form data changes.

    [hasDestructiveData, dispatch],
  );

  // When an external navigator sets pendingNavigation, open the modal.
  // We blur first, then defer setModalVisible to the next animation frame
  // so focus has fully left #content before va-modal sets aria-hidden on it.
  useEffect(() => {
    if (pendingNavigation && !modalVisible) {
      externalTriggerRef.current = true;
      setTriggerOrigin(
        pendingNavigationTriggerOrigin ??
          DESTRUCTIVE_NAV_TRIGGER_ORIGINS.SIDENAV,
      );
      document.activeElement?.blur();
      requestAnimationFrame(() => {
        setModalVisible(true);
      });
    }
  }, [pendingNavigation, pendingNavigationTriggerOrigin, modalVisible]);

  /**
   * Call in the page's submit handler. Opens the modal if there is
   * destructive data; otherwise calls `onProceed`.
   */
  const triggerIfNeeded = useCallback(() => {
    if (hasDestructiveData()) {
      externalTriggerRef.current = false;
      setTriggerOrigin(DESTRUCTIVE_NAV_TRIGGER_ORIGINS.CONTINUE);
      document.activeElement?.blur();
      requestAnimationFrame(() => {
        setModalVisible(true);
      });
      return true;
    }

    setAlertVisible(false);
    if (onProceed) onProceed();
    return false;
  }, [hasDestructiveData, onProceed]);

  /**
   * Call on primary button click. Runs onConfirm, hides the modal,
   * and shows the success alert.
   */
  const confirmAndDelete = useCallback(() => {
    if (externalTriggerRef.current) {
      // Clear pendingNavigation before hiding the modal to avoid re-open races.
      dispatch(setPendingDestructiveNavigation({}));
      externalTriggerRef.current = false;
    }

    onConfirm();
    setModalVisible(false);
    setAlertVisible(true);
  }, [onConfirm, dispatch]);

  /**
   * Call on secondary button click or modal close. Runs onCancel and
   * hides the modal. If triggered externally, clears pendingNavigation.
   */
  const cancelAndRevert = useCallback(() => {
    onCancel();

    if (externalTriggerRef.current) {
      // Clear pendingNavigation BEFORE hiding the modal so the useEffect
      // that watches pendingNavigation doesn't re-open it inside the rAF
      // window caused by the open effect seeing !modalVisible while
      // pendingNavigation is still set.
      dispatch(setPendingDestructiveNavigation({}));
      externalTriggerRef.current = false;
    }

    setModalVisible(false);
  }, [onCancel, dispatch]);

  /**
   * Call to dismiss the success alert.
   */
  const dismissAlert = useCallback(() => {
    setAlertVisible(false);
  }, []);

  return {
    modalVisible,
    alertVisible,
    isSidenavTriggered:
      triggerOrigin === DESTRUCTIVE_NAV_TRIGGER_ORIGINS.SIDENAV,
    triggerIfNeeded,
    confirmAndDelete,
    cancelAndRevert,
    dismissAlert,
  };
};

export default useDestructiveConfirmation;
